/* MP PRODUCT GALLERY — assets/mp-product-gallery.js
   Drives sections/mp-product-gallery.liquid. Same continuous-marquee
   behaviour as the stock gallery carousel (Swiper autoplay with delay 0 and
   a long linear speed), with two differences:
     1. Pan/swipe is ENABLED (stock sets allowTouchMove: false) — dragging
        pauses the marquee, and it resumes on its own after data-resume
        seconds of idle.
     2. Vanilla JS (no jQuery). Swiper comes from the theme's global
        swiper-bundle. */

;(() => {
	const initGalleries = () => {
		document.querySelectorAll('.mp-pgallery').forEach((root) => {
			if (root.__mpPgalleryInit) return
			const container = root.querySelector('.mp-pgallery__swiper')
			const slideCount = root.querySelectorAll('.mp-pgallery__slide').length
			if (!container || slideCount === 0 || typeof Swiper === 'undefined') return
			root.__mpPgalleryInit = true

			const speed = (parseFloat(root.dataset.speed) || 6) * 1000
			const resumeDelay = (parseFloat(root.dataset.resume) || 3) * 1000

			/* Pin the carousel-height setting in PIXELS so mobile browser
			   chrome collapsing/expanding during scroll can't resize the
			   strip (viewport units track it; a px value doesn't). Re-pin
			   only on genuine width changes (rotation, window resize) —
			   height-only resize events on phones ARE the chrome moving. */
			const heightVh = parseFloat(root.dataset.heightVh) || 0
			if (heightVh > 0) {
				/* Measure the LAYOUT viewport (documentElement.client*), never
				   window.inner* — the latter tracks the VISUAL viewport, which
				   pinch zoom shrinks in CSS pixels. Pinning against a
				   pinch-zoomed height left the strip tiny after zooming out.
				   The layout viewport ignores pinch zoom entirely and is also
				   steadier against mobile browser chrome collapse. */
				const layoutHeight = () => document.documentElement.clientHeight
				const layoutWidth = () => document.documentElement.clientWidth
				const pinHeight = () => {
					root.style.setProperty(
						'--mp-pgallery-h',
						Math.round(layoutHeight() * (heightVh / 100)) + 'px'
					)
				}
				pinHeight()
				let lastWidth = layoutWidth()
				window.addEventListener('resize', () => {
					// Never re-pin while pinch-zoomed — zoom is not a resize.
					if (window.visualViewport && window.visualViewport.scale !== 1) return
					const width = layoutWidth()
					if (width === lastWidth) return
					lastWidth = width
					pinHeight()
				})
			}

			const swiper = new Swiper(container, {
				loop: true,
				speed: speed,
				spaceBetween: 8,
				slidesPerView: 1.5,
				allowTouchMove: true,
				grabCursor: true,
				longSwipesRatio: 0.1,
				autoplay: {
					delay: 0,
					disableOnInteraction: true,
				},
				breakpoints: {
					750: {
						slidesPerView: 2.68,
						spaceBetween: 16,
					},
				},
			})

			let resumeTimer
			let startTimer
			let userStopped = false

			const freeze = () => {
				clearTimeout(resumeTimer)
				clearTimeout(startTimer)
				if (!swiper.slides || swiper.slides.length === 0) return
				swiper.setTranslate(swiper.getTranslate())
				swiper.autoplay.stop()
			}

			/* Glide from wherever the strip stopped back onto the marquee
			   rhythm: finish the in-progress slide at a proportional duration,
			   then hand back to autoplay (same math as the stock carousel). */
			const resume = () => {
				if (userStopped) return
				const slide = swiper.slides && swiper.slides[swiper.activeIndex]
				if (!slide || slide.offsetWidth === 0) return
				const ratio = Math.min(
					Math.abs(
						(slide.offsetWidth * swiper.activeIndex + swiper.getTranslate()) /
							slide.offsetWidth
					),
					1
				)
				const duration = swiper.params.speed * ratio
				swiper.slideTo(swiper.activeIndex, duration)
				clearTimeout(startTimer)
				startTimer = setTimeout(() => {
					if (!userStopped) swiper.autoplay.start()
				}, duration)
			}

			// Pan/swipe: Swiper stops autoplay on touch (disableOnInteraction);
			// once the user lets go and stays idle, pick the marquee back up.
			swiper.on('touchStart', () => {
				clearTimeout(resumeTimer)
				clearTimeout(startTimer)
			})
			swiper.on('touchEnd', () => {
				clearTimeout(resumeTimer)
				resumeTimer = setTimeout(resume, resumeDelay)
			})

			const stopButton = root.querySelector('.mp-pgallery__stop')
			if (stopButton) {
				stopButton.addEventListener('click', () => {
					userStopped = !userStopped
					stopButton.classList.toggle('is-stopped', userStopped)
					stopButton.setAttribute('aria-pressed', String(userStopped))
					if (userStopped) freeze()
					else resume()
				})
			}

			/* ----------------------------------------------------------------
			   Lightbox (enable_lightbox setting). Slides carry data-mp-index /
			   data-mp-full / data-mp-alt; loop duplicates share an index, so a
			   click on any copy opens the right image. Swiper's allowClick
			   flag separates real taps from the end of a drag. */
			if (root.dataset.lightbox === 'true') {
				/* The lightbox img is capped at min(92vw, 120rem); 92vw is exact on
				   phones and only slightly over on wide desktops, which costs one
				   variant step at worst. */
				const LIGHTBOX_SIZES = '92vw'

				const gallery = []
				root.querySelectorAll('.mp-pgallery__slide[data-mp-full]').forEach((slide) => {
					const index = parseInt(slide.dataset.mpIndex, 10)
					if (!gallery[index]) {
						gallery[index] = {
							src: slide.dataset.mpFull,
							srcset: slide.dataset.mpFullset || '',
							alt: slide.dataset.mpAlt || ''
						}
					}
				})

				let overlay = null
				let current = 0
				let lastFocused = null
				let renderToken = 0

				/* The carousel and the lightbox request different variants of the
				   same image, so opening the lightbox is ALWAYS a cold download —
				   never a cache hit. Find whichever copy of this slide the browser
				   has already decoded (loop duplicates share an index) and paint
				   that first; it is instant, and the full-res swaps in on load. */
				const cachedThumb = (index) => {
					const imgs = root.querySelectorAll(
						'.mp-pgallery__slide[data-mp-index="' + index + '"] img'
					)
					for (const el of imgs) {
						if (el.complete && (el.currentSrc || el.src)) return el.currentSrc || el.src
					}
					return ''
				}

				/* Warm a neighbour through a detached Image carrying the same
				   srcset/sizes, so the variant it caches is the one the lightbox
				   will actually pick. */
				const preload = (index) => {
					const item = gallery[index]
					if (!item || item.preloaded) return
					item.preloaded = true
					const warm = new Image()
					warm.sizes = LIGHTBOX_SIZES
					if (item.srcset) warm.srcset = item.srcset
					warm.src = item.src
				}

				const renderImage = () => {
					const item = gallery[current]
					if (!item) return
					const img = overlay.querySelector('.mp-pgallery-lightbox__img')
					const token = ++renderToken

					const showFull = () => {
						// A later navigation already won the race — don't overwrite it.
						if (token !== renderToken || !overlay) return
						img.sizes = LIGHTBOX_SIZES
						img.srcset = item.srcset
						img.src = item.src
						img.alt = item.alt
					}

					const probe = new Image()
					probe.sizes = LIGHTBOX_SIZES
					if (item.srcset) probe.srcset = item.srcset
					probe.src = item.src

					if (probe.complete) {
						showFull()
					} else {
						const thumb = cachedThumb(current)
						if (thumb) {
							img.srcset = ''
							img.sizes = ''
							img.src = thumb
							img.alt = item.alt
						}
						probe.addEventListener('load', showFull, { once: true })
						probe.addEventListener('error', showFull, { once: true })
					}

					overlay.querySelector('.mp-pgallery-lightbox__count').textContent =
						current + 1 + ' / ' + gallery.length

					if (gallery.length > 1) {
						preload((current + 1) % gallery.length)
						preload((current - 1 + gallery.length) % gallery.length)
					}
				}

				const step = (delta) => {
					current = (current + delta + gallery.length) % gallery.length
					renderImage()
				}

				/* Focus trap. role="dialog" + aria-modal + focus restore were all
				   present, but nothing held Tab inside the overlay — eight presses
				   walked out into a page the user cannot see (skip link, menu,
				   cart, the gallery triggers underneath). aria-modal scopes screen
				   readers; only a Tab handler scopes the keyboard.
				   getClientRects() rather than offsetParent for the visible check:
				   offsetParent is null for position:fixed elements even when they
				   are on screen. The prev/next buttons are legitimately hidden at
				   <=749px and on single-image galleries, so the set is recomputed
				   per keypress rather than cached at open. */
				const FOCUSABLE_SELECTOR =
					'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

				const focusableItems = () =>
					Array.from(overlay.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
						(el) => !el.hidden && el.getClientRects().length > 0
					)

				const trapTab = (event) => {
					const items = focusableItems()
					if (items.length === 0) {
						event.preventDefault()
						return
					}
					const first = items[0]
					const last = items[items.length - 1]
					if (!overlay.contains(document.activeElement)) {
						event.preventDefault()
						first.focus()
					} else if (event.shiftKey && document.activeElement === first) {
						event.preventDefault()
						last.focus()
					} else if (!event.shiftKey && document.activeElement === last) {
						event.preventDefault()
						first.focus()
					}
				}

				const onKeydown = (event) => {
					if (event.key === 'Escape') closeLightbox()
					if (event.key === 'ArrowLeft') step(-1)
					if (event.key === 'ArrowRight') step(1)
					if (event.key === 'Tab') trapTab(event)
				}

				/* Scroll lock. `overflow: hidden` on <body> does NOT hold on iOS
				   WebKit — the page kept scrolling behind the open lightbox,
				   which fights the horizontal swipe navigation. The theme's own
				   lockScroll() (global.js) does it properly: position-fixed body
				   via .scroll-locked, offset carried in --scroll, scrollbar-width
				   compensation so desktop doesn't jump, and it repositions the
				   sticky header. Prefer it; keep an MP-owned fallback so a theme
				   update that renames it degrades to a working lock rather than
				   to no lock at all. Record which path was taken so lock and
				   unlock can never disagree. */
				let usedThemeScrollLock = false
				let fallbackScrollY = 0

				const lockPageScroll = () => {
					usedThemeScrollLock =
						typeof window.lockScroll === 'function' &&
						typeof window.unlockScroll === 'function'
					if (usedThemeScrollLock) {
						window.lockScroll()
						return
					}
					fallbackScrollY = window.scrollY || window.pageYOffset || 0
					document.body.style.top = -fallbackScrollY + 'px'
					document.body.classList.add('mp-pgallery-scroll-locked')
				}

				const unlockPageScroll = () => {
					if (usedThemeScrollLock) {
						window.unlockScroll()
						return
					}
					document.body.classList.remove('mp-pgallery-scroll-locked')
					document.body.style.top = ''
					window.scrollTo(0, fallbackScrollY)
				}

				const closeLightbox = () => {
					if (!overlay) return
					overlay.remove()
					overlay = null
					document.removeEventListener('keydown', onKeydown)
					unlockPageScroll()
					if (lastFocused) lastFocused.focus()
					clearTimeout(resumeTimer)
					resumeTimer = setTimeout(resume, resumeDelay)
				}

				const openLightbox = (index) => {
					current = index
					lastFocused = document.activeElement
					freeze()

					overlay = document.createElement('div')
					overlay.className = 'mp-pgallery-lightbox'
					overlay.setAttribute('role', 'dialog')
					overlay.setAttribute('aria-modal', 'true')
					overlay.setAttribute('aria-label', 'Image gallery')
					overlay.innerHTML =
						'<button type="button" class="mp-pgallery-lightbox__btn mp-pgallery-lightbox__close" aria-label="Close">' +
						'<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1L11 11M11 1L1 11" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>' +
						'</button>' +
						'<button type="button" class="mp-pgallery-lightbox__btn mp-pgallery-lightbox__prev" aria-label="Previous image">' +
						'<svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M7 1L2 6L7 11" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
						'</button>' +
						'<img class="mp-pgallery-lightbox__img" src="" alt="">' +
						'<button type="button" class="mp-pgallery-lightbox__btn mp-pgallery-lightbox__next" aria-label="Next image">' +
						'<svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1L6 6L1 11" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
						'</button>' +
						'<span class="mp-pgallery-lightbox__count" aria-hidden="true"></span>'

					overlay.querySelector('.mp-pgallery-lightbox__close').addEventListener('click', closeLightbox)
					overlay.querySelector('.mp-pgallery-lightbox__prev').addEventListener('click', () => step(-1))
					overlay.querySelector('.mp-pgallery-lightbox__next').addEventListener('click', () => step(1))

					/* Touch swipe navigation (mobile hides the arrows). Mouse is
					   excluded — desktop uses the arrows. A swipe suppresses the
					   backdrop-click close that the browser synthesises after it. */
					let swipeStartX = null
					let swipeStartY = null
					let lastSwipeAt = 0
					overlay.addEventListener('pointerdown', (event) => {
						if (event.pointerType === 'mouse') return
						swipeStartX = event.clientX
						swipeStartY = event.clientY
					})
					overlay.addEventListener('pointerup', (event) => {
						if (swipeStartX === null) return
						const dx = event.clientX - swipeStartX
						const dy = event.clientY - swipeStartY
						swipeStartX = null
						swipeStartY = null
						if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy)) {
							lastSwipeAt = Date.now()
							step(dx < 0 ? 1 : -1)
						}
					})

					overlay.addEventListener('click', (event) => {
						if (event.target !== overlay) return
						if (Date.now() - lastSwipeAt < 400) return
						closeLightbox()
					})
					document.addEventListener('keydown', onKeydown)

					if (gallery.length < 2) {
						overlay.querySelector('.mp-pgallery-lightbox__prev').hidden = true
						overlay.querySelector('.mp-pgallery-lightbox__next').hidden = true
					}

					renderImage()
					document.body.appendChild(overlay)
					lockPageScroll()
					overlay.querySelector('.mp-pgallery-lightbox__close').focus()
				}

				root.querySelector('.mp-pgallery__wrapper').addEventListener('click', (event) => {
					const slide = event.target.closest('.mp-pgallery__slide[data-mp-full]')
					// allowClick is false when the click is really the tail of a drag
					if (!slide || !swiper.allowClick) return
					openLightbox(parseInt(slide.dataset.mpIndex, 10))
				})
			}
		})
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initGalleries)
	} else {
		initGalleries()
	}
	document.addEventListener('shopify:section:load', initGalleries)
})()
